'use client'

import { log } from "@libs/utilsMsg";
import type { IParamsClientAPI, IRequestParamsClient, IRespData, IAxResponse } from "msgdsk-types";

const _requestApi = async ( params:IRequestParamsClient ):Promise<IAxResponse> => {
	const isPost = params.action === "save_msg";
	const payload = { ...params.params, action: params.action, url: params.url } as Record<string, string>;
	if (params.sess_id) payload.sess_id = params.sess_id;

	try {
		log("_requestApi payload:", payload);

		const response = isPost
			? await fetch(`/api/${params.action}`, {
				method: "POST",
				credentials: "same-origin",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify(payload),
			})
			: await fetch(`/api/${params.action}?${new URLSearchParams(payload as Record<string, string>)}`, {
				method: "GET",
				credentials: "same-origin",
			});

		if (!response.ok) {
			return {
				ok: false,
				data: response.statusText,
				status: response.status
			}
		}

		const data = await response.json();

		log("_requestApi resp:", data);

		return {
			ok: true,
			data: data,
			status: response.status
		}
	} catch (error: any) {
		log("_requestApi err:", error);

		return {
			ok: false,
			data: error?.message ?? "request failed",
			status: 0
		}
	}
}

export const apiSendLoginEmailPin = async ( email:string, url:string, captcha_str:string = "" ):Promise<any> => { // SEND EMAIL LOGIN CODE
	const reqParams:IRequestParamsClient = {
		action: "email_pin",
		url: url,
		sess_id: '',
		// captcha_str: the pin-send is captcha-gated (docs/31 §13) — the server
		// compares it against the session's captcha_str and consumes it, so a
		// failed attempt needs a fresh image.
		params: { email: email, captcha_str: captcha_str }
	};
	
	const resp:IAxResponse = await _requestApi(reqParams);

	if (typeof resp.data === "string" || Array.isArray(resp.data)) resp.data = { result: "err" };

	const result = resp.data;

	log("apiClient: apiSendLoginEmailPin:", resp);

	return result.result === "err" ? null : { msg_id: result.result };
}

// LOCKED-SCREEN email login (2026-07-18): the scoped, NO-CAPTCHA send. The
// server mails a code only when `email` is this message's own login email, and
// always returns the same opaque body (the desk's encoded msg_id) so the
// response is not an oracle. The pin step then verifies through the ordinary
// email login (enterWithEmail) with the returned msg_id.
export const apiSendMsgLoginCode = async ( codekey:string, email:string, url:string ):Promise<{ msg_id:string } | null> => {
	const reqParams:IRequestParamsClient = {
		action: "msg_login_code",
		url: url,
		sess_id: '',
		params: { email: email, code_key: codekey }
	};

	const resp:IAxResponse = await _requestApi(reqParams);

	if (!resp.ok) return null;
	const data = resp.data as { result?: unknown } | undefined;
	const msgId = data && typeof data.result === "string" ? data.result : "";

	log("apiClient: apiSendMsgLoginCode:", resp);

	return msgId ? { msg_id: msgId } : null;
}

export const apiCheckNamekey = async ( name_key:string, sess_id:string, url:string ):Promise<string> => { // CHECK KEY & GET MSG DATA
	const reqParams:IRequestParamsClient = {
		action: "check_namekey",
		url: url,
		sess_id: sess_id,
		params: { name_key: name_key }
	};
	
	const resp:IAxResponse = await _requestApi(reqParams);
	const { data: result }:IRespData = resp;	
	
	// log("apiClient: checkNameKey", [reqParams, resp]);
	
	if (typeof result === "string" || Array.isArray(result)) return "";
	if (!result?.ok || typeof result?.data !== "string" || result?.data === "0") return "";	
	
	return result.data;
}

export type ApiSaveMsgResult =
	| { ok: true; data: string[] }
	| { ok: false; error: string };

export const apiSaveMsg = async (sess_id:string, url:string, params:IParamsClientAPI):Promise<ApiSaveMsgResult> => { // SAVE MSG
	const reqParams:IRequestParamsClient = {
		action: "save_msg",
		url: url,
		sess_id: sess_id,
		params: params, 
	};

	const resp:IAxResponse = await _requestApi(reqParams);
	const result = resp.data as { ok?: boolean; data?: unknown } | string | string[] | undefined;

	// log("apiClient: saveMsg", [reqParams, resp]);
	
	if (!resp.ok) return { ok: false, error: typeof resp.data === 'string' ? resp.data : 'err_save' };
	if (typeof result === "string" || Array.isArray(result) || !result) {
		return { ok: false, error: typeof result === 'string' ? result : 'err_save' };
	}
	if (!result.ok || !Array.isArray(result.data)) {
		return { ok: false, error: typeof result.data === 'string' ? result.data : 'err_save' };
	}

	return { ok: true, data: result.data as string[] };
}

/* export const apiCheckKey = async ( namekey:string, sess_id:string, url:string ):Promise<any> => { // CHECK KEY & GET MSG DATA
	const params:IRequestParamsClient = {
		action: "check_msg",
		url: url,
		sess_id: sess_id,
		params: { name_key: namekey }
	};
	
	const resp = await _requestApi(params);

	const is_found = resp.ok && typeof resp.data === "string" && resp.data !== "0";

	log("apiClient: checkKey", [params, resp]);

	return is_found ? resp.data : null;
}
*/
/* export const apiLoadMsg = async ( codeKey:string, sess_id:string, url:string ):Promise<any> => { // LOAD MSG BY CODEKEY
	const params:IRequestParamsClient = {
		action: "load_msg",
		url: url,
		sess_id: sess_id,
		params: { code_key: codeKey, pin: "" }
	};
	
	const resp = await _requestApi(params);

	log("apiClient: loadMsg", [params, resp]);

	//if (typeof resp.data === "string" || !Array.isArray(resp.data)) return;

	return resp.data;
} 
 */
